/** Query parameter carrying a safe post-login application destination. */
export const LOGIN_REDIRECT_QUERY_KEY = 'redirect';

/** Pathname of the password-login page. */
export const LOGIN_PATHNAME = '/login';

/** Safe destination used when a requested post-login destination is invalid. */
export const LOGIN_REDIRECT_FALLBACK = '/';

/** Base origin used only to parse and validate path-absolute application destinations. */
const VALIDATION_ORIGIN = 'https://login-redirect.invalid';

/** Matches percent characters that do not begin a complete percent-encoded byte. */
const MALFORMED_PERCENT_ENCODING = /%(?![0-9A-Fa-f]{2})/;

/** Matches raw ASCII controls that URL parsing may strip before host resolution. */
const ASCII_CONTROL_CHARACTER = /[\u0000-\u001F\u007F]/;

/**
 * Builds a clean same-origin login URL containing one encoded return destination.
 *
 * The caller's URL is not mutated, and none of its existing query parameters are
 * inherited by the returned login URL.
 */
export function buildLoginRedirectUrl(requestUrl: URL, destination: string): URL {
  const loginUrl = new URL(LOGIN_PATHNAME, requestUrl.origin);
  loginUrl.searchParams.set(LOGIN_REDIRECT_QUERY_KEY, destination);
  return loginUrl;
}

/**
 * Returns a single safe path-absolute login destination or the root fallback.
 *
 * Duplicate, malformed, external, fragment-bearing, and login-loop destinations
 * are rejected without throwing so untrusted query text never reaches navigation.
 */
export function validateLoginRedirectDestination(searchParams: URLSearchParams): string {
  try {
    const destinations = searchParams.getAll(LOGIN_REDIRECT_QUERY_KEY);
    if (destinations.length !== 1) {
      return LOGIN_REDIRECT_FALLBACK;
    }

    const destination = destinations[0];
    if (
      !destination ||
      !destination.startsWith('/') ||
      destination.startsWith('//') ||
      destination.includes('#') ||
      destination.includes('\\') ||
      ASCII_CONTROL_CHARACTER.test(destination) ||
      MALFORMED_PERCENT_ENCODING.test(destination)
    ) {
      return LOGIN_REDIRECT_FALLBACK;
    }

    const parsedDestination = new URL(destination, VALIDATION_ORIGIN);
    if (parsedDestination.origin !== VALIDATION_ORIGIN) {
      return LOGIN_REDIRECT_FALLBACK;
    }

    if (
      parsedDestination.pathname === LOGIN_PATHNAME ||
      parsedDestination.pathname.startsWith(`${LOGIN_PATHNAME}/`)
    ) {
      return LOGIN_REDIRECT_FALLBACK;
    }

    return destination;
  } catch {
    return LOGIN_REDIRECT_FALLBACK;
  }
}
